import React from "react";
import { Button, Card, Empty, Space, Tooltip, Typography } from "antd";
import Table from 'components/Common/Table';
import { EditOutlined, MailOutlined, PhoneOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const { Text } = Typography;

const formatDate = (value) => (value ? dayjs(value).format("DD-MM-YYYY") : "-");

/**
 * Table of contacts, shared by the account/vendor contact cards and the
 * accounts list's expanded rows.
 *
 * `onEdit(contact)` edits in place (e.g. a drawer); without it the edit icon
 * goes to the contact page.
 */
export const ContactsTable = ({ contacts = [], onEdit, loading = false }) => {
  const navigate = useNavigate();

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, row) =>
        [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(" ") || "-",
    },
    { title: "Job Title", dataIndex: "job_title", render: (v) => v || "-" },
    {
      title: "Email",
      dataIndex: "email",
      render: (value) =>
        value ? (
          <Space size={4}>
            <MailOutlined style={{ color: "#8c8c8c" }} />
            <a href={`mailto:${value}`}>{value}</a>
          </Space>
        ) : (
          "-"
        ),
    },
    {
      title: "Phone",
      dataIndex: "phone_number",
      render: (value) =>
        value ? (
          <Space size={4}>
            <PhoneOutlined style={{ color: "#8c8c8c" }} />
            <a href={`tel:${value}`}>{value}</a>
          </Space>
        ) : (
          "-"
        ),
    },
    { title: "Birth Date", dataIndex: "birth_date", render: formatDate },
    { title: "Anniversary", dataIndex: "marriage_anniversary_date", render: formatDate },
    {
      title: "",
      key: "actions",
      width: 48,
      render: (_, row) => (
        <Tooltip title="Edit contact">
          <a onClick={() => (onEdit ? onEdit(row) : navigate(`/contacts/edit/${row.id}`))}>
            <EditOutlined />
          </a>
        </Tooltip>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      size="small"
      loading={loading}
      dataSource={contacts}
      columns={columns}
      pagination={false}
      scroll={{ x: "max-content" }}
    />
  );
};

/**
 * The people attached to an account or a vendor.
 *
 * Both records nest their `contacts` on the detail endpoint, so this only
 * renders what the parent already loaded — no extra request.
 *
 * `onAdd` / `onEdit` let the parent handle adding and editing in place; without
 * them the buttons navigate (`createHref`, the contact page).
 */
const RelatedContactsCard = ({ contacts = [], title = "Contacts", createHref, onAdd, onEdit, bordered = false }) => {
  const navigate = useNavigate();
  const canAdd = Boolean(onAdd || createHref);

  return (
    <Card
      title={
        <Space size={8}>
          {title}
          {contacts.length > 0 && <Text type="secondary">({contacts.length})</Text>}
        </Space>
      }
      bordered={bordered}
      extra={
        canAdd && (
          <Button type="link" icon={<PlusOutlined />} onClick={() => (onAdd ? onAdd() : navigate(createHref))}>
            Add Contact
          </Button>
        )
      }
      styles={{ body: { paddingTop: 0 } }}
    >
      {contacts.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No contacts linked yet"
          style={{ padding: "12px 0" }}
        />
      ) : (
        <ContactsTable contacts={contacts} onEdit={onEdit} />
      )}
    </Card>
  );
};

export default RelatedContactsCard;
